/**
 * Store Info > Photos and Documents at /stores/setup/info/:store_no/files.
 *
 * The folder and file manager behind the Photos and Documents tile on the
 * Store Info page: one tree per store, with create, rename, move, upload
 * (files or a whole folder), a tile or list view remembered per browser,
 * per-file notes, and delete behind a warning that shows what it would take
 * with it. A sub-page of Store Info, not a catalog entry, so it carries its
 * own top-left back link and is gated by the same stores.info slug.
 *
 * The data layer is ui/src/lib/useStoreFiles.ts and the upload queue is
 * storeFilesUpload.ts; the plain functions (byte units, the remembered view,
 * the folder-tree plan) are storeFilesUtils.ts so they can be unit-tested.
 * Until the /v1/stores/info/* routes deploy (api#698) every call 404s and the
 * page shows the standard "not available yet" card, like its parent.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  ExternalLink,
  File as FileIcon,
  FileArchive,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderPlus,
  FolderUp,
  Image as ImageIcon,
  LayoutGrid,
  List,
  MessageSquare,
  Pencil,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Modal } from '@/components/Modal';
import { RowIconButton } from '@/components/RowActionIcons';
import { formatDateTime } from '@/lib/dateUtils';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { isNotAvailable, storeLabel } from '@/lib/useStores';
import { useSession } from '@/lib/useSession';
import { useStoreInfoStores } from '@/lib/useStoreInfo';
import {
  fetchStoreFileUrl,
  useAddStoreFileNote,
  useCreateStoreFolder,
  useDeleteStoreFile,
  useDeleteStoreFolder,
  usePatchStoreFile,
  useRenameStoreFolder,
  useStoreFileNotes,
  useStoreFolderCount,
  useStoreFolderListing,
  type StoreFileRow,
  type StoreFolder,
  type StoreFolderCrumb,
} from '@/lib/useStoreFiles';
import { useStoreFileUploads, type UploadItem } from './storeFilesUpload';
import {
  fileKind,
  folderNameError,
  formatBytes,
  isImageContentType,
  readStoreFilesView,
  writeStoreFilesView,
  type FileKind,
  type StoreFilesView,
} from './storeFilesUtils';
import {
  AccessDeniedCard,
  BackLink,
  ErrorCard,
  NotAvailableCard,
  StoresHeader,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './storesShared';

const CARD_CLS = 'rounded-lg border border-line bg-surface shadow-card';
const DANGER_BTN_CLS =
  'inline-flex items-center gap-1 text-xs border border-red-700 bg-red-700 text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60';
const MUTED_CLS = 'text-amp-ink-muted dark:text-amp-dark-ink-muted';

const KIND_ICONS: Record<FileKind, typeof FileIcon> = {
  image: ImageIcon,
  pdf: FileText,
  sheet: FileSpreadsheet,
  doc: FileText,
  archive: FileArchive,
  file: FileIcon,
};

type Toast = { kind: 'ok' | 'err'; text: string };

/** What the one open modal is about, if any. */
type Dialog =
  | { kind: 'new-folder' }
  | { kind: 'rename-folder'; folder: StoreFolder }
  | { kind: 'rename-file'; file: StoreFileRow }
  | { kind: 'move-file'; file: StoreFileRow }
  | { kind: 'delete-folder'; folder: StoreFolder }
  | { kind: 'delete-file'; file: StoreFileRow };

/**
 * Opens a file's presigned URL in a new tab. The tab is opened synchronously,
 * before the await, because a popup blocker rejects a window.open that comes
 * back after an async hop.
 */
async function openStoreFile(storeNo: string, fileId: number): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  if (tab) tab.opener = null;
  const url = await fetchStoreFileUrl(storeNo, fileId);
  if (tab) tab.location.href = url;
  else window.location.href = url;
}

export function StoreFiles() {
  const { store_no: storeNoParam } = useParams<{ store_no: string }>();
  const storeNo = storeNoParam ?? null;
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.info', user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const folderParam = searchParams.get('folder');
  const folderId = folderParam && /^\d+$/.test(folderParam) ? Number(folderParam) : null;

  const [view, setView] = useState<StoreFilesView>(readStoreFilesView);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notesFor, setNotesFor] = useState<StoreFileRow | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const storesQ = useStoreInfoStores(canView);
  const listingQ = useStoreFolderListing(storeNo, folderId, canView);
  const uploads = useStoreFileUploads(storeNo);

  const storeName = useMemo(
    () => storesQ.data?.find((s) => s.store_no === storeNo)?.store_name ?? null,
    [storesQ.data, storeNo],
  );

  const goToFolder = useCallback(
    (id: number | null) => {
      setNotesFor(null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id == null) next.delete('folder');
          else next.set('folder', String(id));
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const chooseView = (next: StoreFilesView) => {
    setView(next);
    writeStoreFilesView(next);
  };

  // Toasts clear themselves so the page does not accumulate banners.
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const openFile = useCallback(
    (file: StoreFileRow) => {
      if (!storeNo) return;
      void openStoreFile(storeNo, file.id).catch((err: Error) =>
        setToast({ kind: 'err', text: `Could not open ${file.name}: ${err.message}` }),
      );
    },
    [storeNo],
  );

  if (user && !canView) return <AccessDeniedCard what="Store Info" />;

  const backTo = `/stores/setup/info${storeNo ? `?store=${storeNo}` : ''}`;
  const breadcrumb = listingQ.data?.breadcrumb ?? [];

  const header = (
    <>
      <BackLink to={backTo} label="Back to Store Info" />
      <StoresHeader
        title={storeLabel(storeNo, storeName)}
        subtitle="Photos and documents for this store."
      />
    </>
  );

  if (isNotAvailable(listingQ.error)) {
    return (
      <div>
        {header}
        <NotAvailableCard what="Photos and Documents" />
      </div>
    );
  }

  return (
    <div>
      {header}

      <Breadcrumbs crumbs={breadcrumb} onGo={goToFolder} />

      <Toolbar
        folderId={folderId}
        view={view}
        onView={chooseView}
        onNewFolder={() => setDialog({ kind: 'new-folder' })}
        onFiles={(files) => uploads.addFiles(files, folderId)}
        onFolder={(files) => uploads.addFolder(files, folderId)}
      />

      {uploads.items.length > 0 && (
        <UploadPanel items={uploads.items} onRetry={uploads.retry} onClear={uploads.clearFinished} />
      )}

      {listingQ.error ? (
        <ErrorCard message={listingQ.error.message} />
      ) : listingQ.isLoading ? (
        <div className="h-64 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
      ) : (
        <Listing
          storeNo={storeNo}
          view={view}
          folderId={folderId}
          folders={listingQ.data?.folders ?? []}
          files={listingQ.data?.files ?? []}
          notesFor={notesFor}
          onOpenFolder={goToFolder}
          onOpenFile={openFile}
          onNotes={(file) => setNotesFor((prev) => (prev?.id === file.id ? null : file))}
          onDialog={setDialog}
        />
      )}

      {view === 'tile' && notesFor && storeNo && (
        <NotesSheet
          storeNo={storeNo}
          file={notesFor}
          folderId={folderId}
          onClose={() => setNotesFor(null)}
        />
      )}

      {dialog && storeNo && (
        <Dialogs
          dialog={dialog}
          storeNo={storeNo}
          folderId={folderId}
          folders={listingQ.data?.folders ?? []}
          breadcrumb={breadcrumb}
          onClose={() => setDialog(null)}
          onDone={(text) => {
            setDialog(null);
            setToast({ kind: 'ok', text });
          }}
        />
      )}

      {toast && <ToastBanner toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// --------------------------------------------------------------------------
// Header pieces
// --------------------------------------------------------------------------

function Breadcrumbs({
  crumbs,
  onGo,
}: {
  crumbs: StoreFolderCrumb[];
  onGo: (id: number | null) => void;
}) {
  return (
    <nav aria-label="Folders" className="mb-4 flex flex-wrap items-center gap-1 text-sm">
      <button
        type="button"
        onClick={() => onGo(null)}
        className={`${MUTED_CLS} hover:text-amp-ink dark:hover:text-amp-dark-ink`}
      >
        All files
      </button>
      {crumbs.map((crumb, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={crumb.id} className="flex items-center gap-1">
            <ChevronRight className={`h-3 w-3 ${MUTED_CLS}`} aria-hidden />
            {last ? (
              <span className="font-medium text-amp-ink dark:text-amp-dark-ink">{crumb.name}</span>
            ) : (
              <button
                type="button"
                onClick={() => onGo(crumb.id)}
                className={`${MUTED_CLS} hover:text-amp-ink dark:hover:text-amp-dark-ink`}
              >
                {crumb.name}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function Toolbar({
  folderId,
  view,
  onView,
  onNewFolder,
  onFiles,
  onFolder,
}: {
  folderId: number | null;
  view: StoreFilesView;
  onView: (v: StoreFilesView) => void;
  onNewFolder: () => void;
  onFiles: (files: File[]) => void;
  onFolder: (files: File[]) => void;
}) {
  const filesRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);

  // Clearing the value lets the same selection be picked again after a retry.
  const take = (input: HTMLInputElement, hand: (files: File[]) => void) => {
    const picked = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (picked.length) hand(picked);
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <button type="button" onClick={onNewFolder} className={secondaryBtnCls}>
        <FolderPlus className="h-3.5 w-3.5" aria-hidden /> New folder
      </button>
      <button type="button" onClick={() => filesRef.current?.click()} className={secondaryBtnCls}>
        <Upload className="h-3.5 w-3.5" aria-hidden /> Upload files
      </button>
      <button type="button" onClick={() => folderRef.current?.click()} className={secondaryBtnCls}>
        <FolderUp className="h-3.5 w-3.5" aria-hidden /> Upload folder
      </button>
      <input
        ref={filesRef}
        type="file"
        multiple
        className="hidden"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => take(e.currentTarget, onFiles)}
      />
      {/* webkitdirectory is non-standard but is what every browser that can
          upload a folder understands; React needs it spelled both ways. */}
      <input
        ref={folderRef}
        type="file"
        multiple
        className="hidden"
        aria-hidden
        tabIndex={-1}
        {...{ webkitdirectory: '', directory: '' }}
        onChange={(e) => take(e.currentTarget, onFolder)}
      />

      <span className={`ml-auto text-xs ${MUTED_CLS}`}>
        {folderId == null ? 'All files' : 'Uploads land in this folder'}
      </span>
      <div className="inline-flex overflow-hidden rounded border border-amp-line dark:border-amp-dark-line">
        <ViewButton icon={LayoutGrid} label="Tile view" active={view === 'tile'} onClick={() => onView('tile')} />
        <ViewButton icon={List} label="List view" active={view === 'list'} onClick={() => onView('list')} />
      </div>
    </div>
  );
}

// The selected view reads as cream text on a dark fill, per COCKPIT.md 2.9.
function ViewButton({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: typeof LayoutGrid;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={
        active
          ? 'px-2.5 py-1.5 bg-amp-ink text-amp-cream dark:bg-amp-dark-ink dark:text-amp-dark-surface'
          : `px-2.5 py-1.5 ${MUTED_CLS} hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm`
      }
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}

// --------------------------------------------------------------------------
// Upload progress
// --------------------------------------------------------------------------

function UploadPanel({
  items,
  onRetry,
  onClear,
}: {
  items: UploadItem[];
  onRetry: (uid: string) => void;
  onClear: () => void;
}) {
  const done = items.filter((it) => it.status === 'done').length;
  return (
    <div className={`${CARD_CLS} mb-4 px-4 py-3`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[15px] font-semibold text-ink">
          Uploads ({done}/{items.length})
        </span>
        {done > 0 && (
          <button type="button" onClick={onClear} className={secondaryBtnCls}>
            Clear finished
          </button>
        )}
      </div>
      <ul className="flex flex-col gap-2">
        {items.map((it) => (
          <li key={it.uid} className="text-xs">
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate text-amp-ink dark:text-amp-dark-ink">
                {it.path ? `${it.path}/` : ''}
                {it.name}
              </span>
              <span className={`shrink-0 ${MUTED_CLS}`}>{formatBytes(it.size)}</span>
            </div>
            {it.status === 'error' ? (
              <div className="mt-1 flex items-center gap-2">
                <span className="text-red-700 dark:text-red-300">{it.error}</span>
                <button type="button" onClick={() => onRetry(it.uid)} className={secondaryBtnCls}>
                  Retry
                </button>
              </div>
            ) : (
              <div
                className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-amp-cream-warm dark:bg-amp-dark-surface-warm"
                role="progressbar"
                aria-label={`Uploading ${it.name}`}
                aria-valuenow={Math.round(it.progress * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className={`h-full rounded-full ${
                    it.status === 'done' ? 'bg-amp-olive-deep' : 'bg-amp-olive'
                  } dark:bg-amp-olive-dark`}
                  style={{ width: `${Math.round(it.progress * 100)}%` }}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// --------------------------------------------------------------------------
// Listing
// --------------------------------------------------------------------------

function Listing({
  storeNo,
  view,
  folderId,
  folders,
  files,
  notesFor,
  onOpenFolder,
  onOpenFile,
  onNotes,
  onDialog,
}: {
  storeNo: string | null;
  view: StoreFilesView;
  folderId: number | null;
  folders: StoreFolder[];
  files: StoreFileRow[];
  notesFor: StoreFileRow | null;
  onOpenFolder: (id: number | null) => void;
  onOpenFile: (file: StoreFileRow) => void;
  onNotes: (file: StoreFileRow) => void;
  onDialog: (dialog: Dialog) => void;
}) {
  if (folders.length === 0 && files.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-amp-line px-4 py-10 text-center dark:border-amp-dark-line">
        <p className={`text-sm ${MUTED_CLS}`}>No files yet. Upload files or create a folder.</p>
      </div>
    );
  }

  if (view === 'tile') {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {folders.map((folder) => (
          <FolderTile
            key={`folder-${folder.id}`}
            folder={folder}
            onOpen={() => onOpenFolder(folder.id)}
            onRename={() => onDialog({ kind: 'rename-folder', folder })}
            onDelete={() => onDialog({ kind: 'delete-folder', folder })}
          />
        ))}
        {files.map((file) => (
          <FileTile
            key={`file-${file.id}`}
            storeNo={storeNo}
            file={file}
            onOpen={() => onOpenFile(file)}
            onNotes={() => onNotes(file)}
            onRename={() => onDialog({ kind: 'rename-file', file })}
            onMove={() => onDialog({ kind: 'move-file', file })}
            onDelete={() => onDialog({ kind: 'delete-file', file })}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={`${CARD_CLS} overflow-hidden`}>
      <ul className="divide-y divide-line">
        {folders.map((folder) => (
          <li key={`folder-${folder.id}`} className="flex items-center gap-3 px-4 py-2.5">
            <Folder className="h-4 w-4 shrink-0 text-amp-olive dark:text-amp-olive-dark" aria-hidden />
            <button
              type="button"
              onClick={() => onOpenFolder(folder.id)}
              className="min-w-0 flex-1 truncate text-left text-sm font-medium text-amp-ink hover:underline dark:text-amp-dark-ink"
            >
              {folder.name}
            </button>
            <span className={`hidden w-40 shrink-0 text-xs sm:block ${MUTED_CLS}`}>Folder</span>
            <span className={`hidden w-52 shrink-0 text-xs md:block ${MUTED_CLS}`}>
              {formatDateTime(folder.created_at)}
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <RowIconButton
                icon={Pencil}
                label="Rename"
                onClick={() => onDialog({ kind: 'rename-folder', folder })}
              />
              <RowIconButton
                icon={Trash2}
                label="Delete"
                onClick={() => onDialog({ kind: 'delete-folder', folder })}
              />
            </span>
          </li>
        ))}
        {files.map((file) => (
          <li key={`file-${file.id}`}>
            <div className="flex items-center gap-3 px-4 py-2.5">
              <FileGlyph file={file} />
              <button
                type="button"
                onClick={() => onOpenFile(file)}
                className="min-w-0 flex-1 truncate text-left text-sm text-amp-ink hover:underline dark:text-amp-dark-ink"
              >
                {file.name}
              </button>
              <span className={`hidden w-20 shrink-0 text-xs sm:block ${MUTED_CLS}`}>
                {formatBytes(file.size_bytes)}
              </span>
              <span className={`hidden w-40 shrink-0 truncate text-xs lg:block ${MUTED_CLS}`}>
                {file.uploaded_by}
              </span>
              <span className={`hidden w-52 shrink-0 text-xs md:block ${MUTED_CLS}`}>
                {formatDateTime(file.created_at)}
              </span>
              <FileActions
                file={file}
                onOpen={() => onOpenFile(file)}
                onNotes={() => onNotes(file)}
                onRename={() => onDialog({ kind: 'rename-file', file })}
                onMove={() => onDialog({ kind: 'move-file', file })}
                onDelete={() => onDialog({ kind: 'delete-file', file })}
              />
            </div>
            {notesFor?.id === file.id && storeNo && (
              <div className="border-t border-line bg-amp-cream-warm px-4 py-3 dark:bg-amp-dark-surface-warm">
                <NotesBody storeNo={storeNo} file={file} folderId={folderId} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FileGlyph({ file }: { file: StoreFileRow }) {
  const Icon = KIND_ICONS[fileKind(file.name, file.content_type)];
  return <Icon className={`h-4 w-4 shrink-0 ${MUTED_CLS}`} aria-hidden />;
}

function FileActions({
  file,
  onOpen,
  onNotes,
  onRename,
  onMove,
  onDelete,
}: {
  file: StoreFileRow;
  onOpen: () => void;
  onNotes: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
}) {
  return (
    <span className="flex shrink-0 items-center gap-2">
      <RowIconButton icon={ExternalLink} label="Open" onClick={onOpen} />
      <RowIconButton icon={Pencil} label="Rename" onClick={onRename} />
      <RowIconButton icon={FolderUp} label="Move to folder" onClick={onMove} />
      <RowIconButton
        icon={MessageSquare}
        label={file.note_count > 0 ? `Notes (${file.note_count})` : 'Notes'}
        onClick={onNotes}
      />
      <RowIconButton icon={Trash2} label="Delete" onClick={onDelete} />
    </span>
  );
}

function FolderTile({
  folder,
  onOpen,
  onRename,
  onDelete,
}: {
  folder: StoreFolder;
  onOpen: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={`${CARD_CLS} flex flex-col overflow-hidden`}>
      <button
        type="button"
        onClick={onOpen}
        className="flex aspect-square w-full items-center justify-center bg-amp-cream-warm hover:opacity-90 dark:bg-amp-dark-surface-warm"
        aria-label={`Open ${folder.name}`}
      >
        <Folder className="h-10 w-10 text-amp-olive dark:text-amp-olive-dark" aria-hidden />
      </button>
      <div className="flex items-start gap-1 px-2.5 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-amp-ink dark:text-amp-dark-ink" title={folder.name}>
            {folder.name}
          </p>
          <p className={`text-xs ${MUTED_CLS}`}>{formatDateTime(folder.created_at)}</p>
        </div>
        <span className="flex shrink-0 items-center gap-1.5">
          <RowIconButton icon={Pencil} label="Rename" onClick={onRename} />
          <RowIconButton icon={Trash2} label="Delete" onClick={onDelete} />
        </span>
      </div>
    </div>
  );
}

function FileTile({
  storeNo,
  file,
  onOpen,
  onNotes,
  onRename,
  onMove,
  onDelete,
}: {
  storeNo: string | null;
  file: StoreFileRow;
  onOpen: () => void;
  onNotes: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={`${CARD_CLS} flex flex-col overflow-hidden`}>
      <button
        type="button"
        onClick={onOpen}
        className="flex aspect-square w-full items-center justify-center overflow-hidden bg-amp-cream-warm hover:opacity-90 dark:bg-amp-dark-surface-warm"
        aria-label={`Open ${file.name}`}
      >
        <FileThumb storeNo={storeNo} file={file} />
      </button>
      <div className="flex items-start gap-1 px-2.5 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-amp-ink dark:text-amp-dark-ink" title={file.name}>
            {file.name}
          </p>
          <p className={`text-xs ${MUTED_CLS}`}>{formatDateTime(file.created_at)}</p>
        </div>
        <span className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          <RowIconButton icon={Pencil} label="Rename" onClick={onRename} />
          <RowIconButton icon={FolderUp} label="Move to folder" onClick={onMove} />
          <RowIconButton
            icon={MessageSquare}
            label={file.note_count > 0 ? `Notes (${file.note_count})` : 'Notes'}
            onClick={onNotes}
          />
          <RowIconButton icon={Trash2} label="Delete" onClick={onDelete} />
        </span>
      </div>
    </div>
  );
}

/**
 * The square thumbnail in tile view. Images get their presigned URL, but only
 * once the tile is actually scrolled into view: a folder of 300 photos would
 * otherwise fire 300 presign calls on mount. Anything else, and any image
 * whose URL cannot be fetched, falls back to the file-type glyph.
 */
function FileThumb({ storeNo, file }: { storeNo: string | null; file: StoreFileRow }) {
  const isImage = isImageContentType(file.content_type);
  const holder = useRef<HTMLSpanElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isImage || !storeNo || url) return;
    const node = holder.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        fetchStoreFileUrl(storeNo, file.id)
          .then((next) => {
            if (!cancelled) setUrl(next);
          })
          .catch(() => {
            if (!cancelled) setFailed(true);
          });
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [file.id, isImage, storeNo, url]);

  if (isImage && url && !failed) {
    return (
      <img
        src={url}
        alt={file.name}
        loading="lazy"
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }

  const Icon = KIND_ICONS[fileKind(file.name, file.content_type)];
  return (
    <span ref={holder} className="flex h-full w-full items-center justify-center">
      <Icon className={`h-10 w-10 ${MUTED_CLS}`} aria-hidden />
    </span>
  );
}

// --------------------------------------------------------------------------
// Notes
// --------------------------------------------------------------------------

function NotesBody({
  storeNo,
  file,
  folderId,
}: {
  storeNo: string;
  file: StoreFileRow;
  folderId: number | null;
}) {
  const notesQ = useStoreFileNotes(storeNo, file.id);
  const add = useAddStoreFileNote(storeNo, file.id, folderId);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onAdd = () => {
    const body = draft.trim();
    if (!body) return;
    setError(null);
    add.mutate(body, {
      onSuccess: () => setDraft(''),
      onError: (err) => setError(err.message),
    });
  };

  return (
    <div>
      <label htmlFor={`file-note-${file.id}`} className={labelCls}>
        Add a note
      </label>
      <textarea
        id={`file-note-${file.id}`}
        rows={2}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="What this file is, or what changed"
        className={inputCls}
      />
      <div className="mt-2">
        <button
          type="button"
          onClick={onAdd}
          disabled={add.isPending || !draft.trim()}
          className={primaryBtnCls}
        >
          {add.isPending ? 'Adding...' : 'Add note'}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-700 dark:text-red-300">Could not add note: {error}</p>}

      <div className="mt-3">
        {notesQ.error ? (
          <p className="text-xs text-red-700 dark:text-red-300">
            Could not load notes: {notesQ.error.message}
          </p>
        ) : notesQ.isLoading ? (
          <div className="h-10 animate-pulse rounded bg-amp-cream dark:bg-amp-dark-surface" />
        ) : (notesQ.data ?? []).length === 0 ? (
          <p className={`text-xs ${MUTED_CLS}`}>No notes yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {(notesQ.data ?? []).map((note) => (
              <li key={note.id} className="py-2">
                <p className="whitespace-pre-wrap text-[14px] text-amp-ink dark:text-amp-dark-ink">
                  {note.body}
                </p>
                <p className={`mt-0.5 text-xs ${MUTED_CLS}`}>
                  {note.author_email} &middot; {formatDateTime(note.created_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Tile view has no row to open under, so notes arrive as a side sheet. */
function NotesSheet({
  storeNo,
  file,
  folderId,
  onClose,
}: {
  storeNo: string;
  file: StoreFileRow;
  folderId: number | null;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" role="presentation" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Notes for ${file.name}`}
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-md overflow-y-auto border-l border-amp-line bg-amp-white p-5 dark:border-amp-dark-line dark:bg-amp-dark-surface"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">Notes</p>
            <h2 className="truncate text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">{file.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`${MUTED_CLS} hover:text-amp-ink dark:hover:text-amp-dark-ink`}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <NotesBody storeNo={storeNo} file={file} folderId={folderId} />
      </aside>
    </div>
  );
}

// --------------------------------------------------------------------------
// Modals
// --------------------------------------------------------------------------

function ModalBody({ children }: { children: ReactNode }) {
  return <div className="px-5 py-4">{children}</div>;
}

function Dialogs({
  dialog,
  storeNo,
  folderId,
  folders,
  breadcrumb,
  onClose,
  onDone,
}: {
  dialog: Dialog;
  storeNo: string;
  folderId: number | null;
  folders: StoreFolder[];
  breadcrumb: StoreFolderCrumb[];
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  switch (dialog.kind) {
    case 'new-folder':
      return <NewFolderModal storeNo={storeNo} folderId={folderId} onClose={onClose} onDone={onDone} />;
    case 'rename-folder':
      return (
        <RenameFolderModal
          storeNo={storeNo}
          folderId={folderId}
          folder={dialog.folder}
          onClose={onClose}
          onDone={onDone}
        />
      );
    case 'rename-file':
      return (
        <RenameFileModal
          storeNo={storeNo}
          folderId={folderId}
          file={dialog.file}
          onClose={onClose}
          onDone={onDone}
        />
      );
    case 'move-file':
      return (
        <MoveFileModal
          storeNo={storeNo}
          folderId={folderId}
          folders={folders}
          breadcrumb={breadcrumb}
          file={dialog.file}
          onClose={onClose}
          onDone={onDone}
        />
      );
    case 'delete-folder':
      return (
        <DeleteFolderModal
          storeNo={storeNo}
          folderId={folderId}
          folder={dialog.folder}
          onClose={onClose}
          onDone={onDone}
        />
      );
    case 'delete-file':
      return (
        <DeleteFileModal
          storeNo={storeNo}
          folderId={folderId}
          file={dialog.file}
          onClose={onClose}
          onDone={onDone}
        />
      );
  }
}

function NameModal({
  title,
  label,
  initial,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  title: string;
  label: string;
  initial: string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(initial);
  const invalid = folderNameError(name);

  return (
    <Modal title={title} eyebrow="Photos and Documents" onClose={onClose} widthClassName="max-w-md">
      <ModalBody>
        <label htmlFor="store-files-name" className={labelCls}>
          {label}
        </label>
        <input
          id="store-files-name"
          type="text"
          autoFocus
          value={name}
          maxLength={255}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !invalid && !busy) onSubmit(name.trim());
          }}
          className={inputCls}
        />
        {error && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!!invalid || busy}
            onClick={() => onSubmit(name.trim())}
            className={primaryBtnCls}
          >
            {busy ? 'Saving...' : 'Save'}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

function NewFolderModal({
  storeNo,
  folderId,
  onClose,
  onDone,
}: {
  storeNo: string;
  folderId: number | null;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const create = useCreateStoreFolder(storeNo);
  const [error, setError] = useState<string | null>(null);
  return (
    <NameModal
      title="New folder"
      label="Folder name"
      initial=""
      busy={create.isPending}
      error={error}
      onClose={onClose}
      onSubmit={(name) => {
        setError(null);
        create.mutate(
          { name, parent_id: folderId },
          {
            onSuccess: () => onDone(`Folder ${name} created.`),
            onError: (err) => setError(`Could not create the folder: ${err.message}`),
          },
        );
      }}
    />
  );
}

function RenameFolderModal({
  storeNo,
  folderId,
  folder,
  onClose,
  onDone,
}: {
  storeNo: string;
  folderId: number | null;
  folder: StoreFolder;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const rename = useRenameStoreFolder(storeNo);
  const [error, setError] = useState<string | null>(null);
  return (
    <NameModal
      title="Rename folder"
      label="Folder name"
      initial={folder.name}
      busy={rename.isPending}
      error={error}
      onClose={onClose}
      onSubmit={(name) => {
        setError(null);
        rename.mutate(
          { id: folder.id, name, parent_id: folderId },
          {
            onSuccess: () => onDone(`Folder renamed to ${name}.`),
            onError: (err) => setError(`Could not rename: ${err.message}`),
          },
        );
      }}
    />
  );
}

function RenameFileModal({
  storeNo,
  folderId,
  file,
  onClose,
  onDone,
}: {
  storeNo: string;
  folderId: number | null;
  file: StoreFileRow;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const patch = usePatchStoreFile(storeNo);
  const [error, setError] = useState<string | null>(null);
  return (
    <NameModal
      title="Rename file"
      label="File name"
      initial={file.name}
      busy={patch.isPending}
      error={error}
      onClose={onClose}
      onSubmit={(name) => {
        setError(null);
        patch.mutate(
          { id: file.id, folder_id: folderId, name },
          {
            onSuccess: () => onDone(`Renamed to ${name}.`),
            onError: (err) => setError(`Could not rename: ${err.message}`),
          },
        );
      }}
    />
  );
}

/**
 * Move to folder. The API lists one level at a time, so the choices are the
 * ones reachable from here without crawling the whole tree: the store root,
 * every folder on the way down (the breadcrumb), and the folders in this one.
 * Moving sideways is two hops (out, then in) rather than a tree picker.
 */
function MoveFileModal({
  storeNo,
  folderId,
  folders,
  breadcrumb,
  file,
  onClose,
  onDone,
}: {
  storeNo: string;
  folderId: number | null;
  folders: StoreFolder[];
  breadcrumb: StoreFolderCrumb[];
  file: StoreFileRow;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const patch = usePatchStoreFile(storeNo);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<string>('');

  const options = useMemo(() => {
    const out: Array<{ value: string; label: string }> = [{ value: '', label: 'All files (top level)' }];
    breadcrumb.slice(0, -1).forEach((crumb) => out.push({ value: String(crumb.id), label: crumb.name }));
    folders.forEach((folder) => out.push({ value: String(folder.id), label: `${folder.name} (in here)` }));
    return out.filter((o) => o.value !== String(folderId ?? ''));
  }, [breadcrumb, folders, folderId]);

  return (
    <Modal title="Move to folder" eyebrow="Photos and Documents" onClose={onClose} widthClassName="max-w-md">
      <ModalBody>
        <p className={`mb-3 text-sm ${MUTED_CLS}`}>Move {file.name} to:</p>
        <label htmlFor="store-files-move" className={labelCls}>
          Folder
        </label>
        <select
          id="store-files-move"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className={inputCls}
        >
          {options.map((o) => (
            <option key={o.value || 'root'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {error && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button
            type="button"
            disabled={patch.isPending}
            onClick={() => {
              setError(null);
              patch.mutate(
                { id: file.id, folder_id: folderId, move_to: target ? Number(target) : null },
                {
                  onSuccess: () => onDone(`${file.name} moved.`),
                  onError: (err) => setError(`Could not move: ${err.message}`),
                },
              );
            }}
            className={primaryBtnCls}
          >
            {patch.isPending ? 'Moving...' : 'Move'}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

function DeleteFileModal({
  storeNo,
  folderId,
  file,
  onClose,
  onDone,
}: {
  storeNo: string;
  folderId: number | null;
  file: StoreFileRow;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const del = useDeleteStoreFile(storeNo);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal title="Delete file" eyebrow="Photos and Documents" onClose={onClose} widthClassName="max-w-md">
      <ModalBody>
        <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
          Delete {file.name}? This cannot be undone.
        </p>
        {error && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button
            type="button"
            disabled={del.isPending}
            onClick={() => {
              setError(null);
              del.mutate(
                { id: file.id, folder_id: folderId },
                {
                  onSuccess: () => onDone(`${file.name} deleted.`),
                  onError: (err) => setError(`Could not delete: ${err.message}`),
                },
              );
            }}
            className={DANGER_BTN_CLS}
          >
            {del.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

/**
 * Folder delete is recursive, so the warning names what goes with it. The
 * button stays disabled until the count lands: nobody confirms a blind
 * recursive delete.
 */
function DeleteFolderModal({
  storeNo,
  folderId,
  folder,
  onClose,
  onDone,
}: {
  storeNo: string;
  folderId: number | null;
  folder: StoreFolder;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const countQ = useStoreFolderCount(storeNo, folder.id);
  const del = useDeleteStoreFolder(storeNo);
  const [error, setError] = useState<string | null>(null);
  const counts = countQ.data;

  return (
    <Modal title="Delete folder" eyebrow="Photos and Documents" onClose={onClose} widthClassName="max-w-md">
      <ModalBody>
        <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
          {counts
            ? `Delete ${folder.name} and its ${counts.files} files in ${counts.folders} folders? This cannot be undone.`
            : countQ.error
              ? `Could not count what is in ${folder.name}: ${countQ.error.message}`
              : `Counting what is inside ${folder.name}...`}
        </p>
        {error && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!counts || del.isPending}
            onClick={() => {
              setError(null);
              del.mutate(
                { id: folder.id, parent_id: folderId },
                {
                  onSuccess: () => onDone(`${folder.name} deleted.`),
                  onError: (err) => setError(`Could not delete: ${err.message}`),
                },
              );
            }}
            className={DANGER_BTN_CLS}
          >
            {del.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

function ToastBanner({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  return (
    <div
      role="status"
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-md border px-3 py-2 text-sm shadow-2 ${
        toast.kind === 'ok'
          ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/40 dark:text-green-200'
          : 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200'
      }`}
    >
      {toast.text}
      <button type="button" onClick={onClose} aria-label="Dismiss">
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
